import { useEffect, useMemo, useState } from "react";
import "./AsbMessagesPage.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import usePageTitle from "./usePageTitle";

const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const formatTime = (value) => {
  if (!value) {
    return "N/A";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }
  return parsed.toLocaleString();
};

const normalizeLineValue = (value) => {
  if (value === null || value === undefined) {
    return [];
  }
  const text = String(value).trim();
  if (!text) {
    return [];
  }
  const values = [text];
  const asNumber = Number(text);
  if (Number.isFinite(asNumber)) {
    values.push(String(asNumber));
  }
  return values;
};

/** Match backend ``vemag_pump_line_no_from_body``: ``Vemag Pump L24`` / ``Vemag Pump Line 23``. */
const VEMAG_PUMP_LINE_RE = /Vemag\s+Pump\s+(?:Line\s+)?L?(\d+)/i;

const extractLineFromVemagSource = (source) => {
  if (typeof source !== "string") {
    return [];
  }
  const match = source.match(VEMAG_PUMP_LINE_RE);
  if (!match) {
    return [];
  }
  return normalizeLineValue(match[1]);
};

const extractLineValues = (payload) => {
  const values = new Set();
  const visited = new Set();

  const walk = (node) => {
    if (node === null || node === undefined) {
      return;
    }
    if (typeof node !== "object") {
      return;
    }
    if (visited.has(node)) {
      return;
    }
    visited.add(node);
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    Object.entries(node).forEach(([key, value]) => {
      const keyLower = key.toLowerCase();
      if (keyLower === "lineno") {
        normalizeLineValue(value).forEach((lineValue) => values.add(lineValue));
      }
      if (keyLower === "source") {
        extractLineFromVemagSource(value).forEach((lineValue) =>
          values.add(lineValue)
        );
      }
      walk(value);
    });
  };

  walk(payload);
  return values;
};

const matchesLineFilter = (body, filterValue) => {
  const trimmed = String(filterValue ?? "").trim();
  if (!trimmed) {
    return true;
  }
  const normalizedFilter = new Set(normalizeLineValue(trimmed));
  if (normalizedFilter.size === 0) {
    return false;
  }
  const lineValues = extractLineValues(body);
  if (lineValues.size === 0) {
    return false;
  }
  for (const value of normalizedFilter) {
    if (lineValues.has(value)) {
      return true;
    }
  }
  return false;
};

const getDistinctLineOptions = (kinds) => {
  const values = new Set();
  kinds.forEach((kind) => {
    const items = Array.isArray(kind.items) ? kind.items : [];
    items.forEach((item) => {
      extractLineValues(item?.body).forEach((lineValue) => {
        const numeric = Number(lineValue);
        if (Number.isFinite(numeric)) {
          values.add(String(numeric));
        }
      });
    });
  });

  return Array.from(values)
    .map((value) => ({
      value,
      numeric: Number(value),
    }))
    .sort((a, b) => a.numeric - b.numeric)
    .map((entry) => entry.value);
};

const normalizePackagesPayload = (body) => {
  if (!body || typeof body !== "object") {
    return [];
  }
  const packs = Array.isArray(body.packages)
    ? body.packages
    : Array.isArray(body.Packs)
    ? body.Packs
    : Array.isArray(body.packs)
    ? body.packs
    : Array.isArray(body.Packages)
    ? body.Packages
    : [];
  return packs.map((pack) => {
    if (!pack || typeof pack !== "object") {
      return { raw: pack };
    }
    return {
      lineNo: pack.lineNo ?? pack.LineNo ?? null,
      productionOrderNo: pack.productionOrderNo ?? pack.ProductionOrderNo ?? null,
      realProductionDate: pack.realProductionDate ?? pack.RealProductionDate ?? null,
      id: pack.id ?? pack.Id ?? pack.InternalId ?? null,
      sku: pack.sku ?? pack.SKU ?? null,
      packWeight: pack.packWeight ?? pack.PackWeight ?? pack.weight ?? pack.Weight ?? null,
      nominalWeight: pack.nominalWeight ?? pack.NominalWeight ?? null,
      status: pack.status ?? pack.Status ?? null,
      raw: pack,
    };
  });
};

function AsbMessagesPage() {
  usePageTitle("ASB Messages");
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [lineFilter, setLineFilter] = useState("");
  const [perKindByKind, setPerKindByKind] = useState({});
  const [refreshIntervalInput, setRefreshIntervalInput] = useState("60");
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const kinds = Array.isArray(payload?.kinds) ? payload.kinds : [];

  const refreshIntervalSec = Number(refreshIntervalInput);
  const refreshIntervalMs =
    Number.isFinite(refreshIntervalSec) && refreshIntervalSec > 0
      ? refreshIntervalSec * 1000
      : 60000;

  const getPerKindValue = (kind) => {
    const raw = perKindByKind[kind];
    if (raw === undefined || raw === null || raw === "") {
      const fallback = kind === "other" ? payload?.per_kind_other : payload?.per_kind;
      return typeof fallback === "number" ? String(fallback) : "5";
    }
    return String(raw);
  };

  const getPerKindNumber = (kind) => {
    const value = Number(getPerKindValue(kind));
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const maxPerKind = kinds
    .filter((kind) => kind.kind !== "other")
    .reduce((max, kind) => {
      const value = getPerKindNumber(kind.kind);
      return value && value > max ? value : max;
    }, 0);
  const perKindParam = maxPerKind > 0 ? maxPerKind : null;
  const perKindOtherParam = getPerKindNumber("other");

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }

    let isMounted = true;
    let intervalId = null;

    const fetchMessages = async () => {
      try {
        const response = await auth.fetchWithAuth("/asb-messages", {
          params: {
            ...(perKindParam ? { per_kind: perKindParam } : {}),
            ...(perKindOtherParam ? { per_kind_other: perKindOtherParam } : {}),
          },
        });
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const data = await response.json();
        if (isMounted) {
          setPayload(data);
          setLastFetchedAt(new Date());
          setError("");
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMessages();
    intervalId = setInterval(fetchMessages, refreshIntervalMs);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    auth.authChecked,
    auth.fetchWithAuth,
    auth.isAuthenticated,
    perKindParam,
    perKindOtherParam,
    refreshIntervalMs,
  ]);

  useEffect(() => {
    if (!payload?.kinds?.length) {
      return;
    }
    setPerKindByKind((prev) => {
      const next = { ...prev };
      let changed = false;
      payload.kinds.forEach((kind) => {
        if (next[kind.kind] === undefined) {
          const fallback = kind.kind === "other" ? payload.per_kind_other : payload.per_kind;
          next[kind.kind] = typeof fallback === "number" ? String(fallback) : "5";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [payload]);
  const filteredKinds = kinds.map((kind) => {
    const items = Array.isArray(kind.items) ? kind.items : [];
    const filteredItems = items.filter((item) =>
      matchesLineFilter(item?.body, lineFilter)
    );
    return {
      ...kind,
      items,
      filteredItems,
      filteredCount: filteredItems.length,
    };
  });
  const hasFilteredMessages = filteredKinds.some(
    (kind) => kind.filteredCount > 0
  );
  const lineOptions = getDistinctLineOptions(kinds);

  return (
    <AuthGate auth={auth} title="ASB Messages Login">
      <div className="asb-page">
        <div className="asb-container">
          <header className="asb-hero">
            <div>
              <p className="asb-eyebrow">Live buffer overview</p>
              <h1 className="asb-title">ASB messages</h1>
              <p className="asb-subtitle">
                Server {payload?.server_name || "N/A"} · Topic {payload?.input_topic || "N/A"}
              </p>
            </div>
            <div className="asb-metrics">
              <div>
                <span>Buffer</span>
                <strong>
                  {payload?.buffer_len ?? 0} / {payload?.buffer_max ?? 0}
                </strong>
              </div>
              <div>
                <span>Updated</span>
                <strong>{formatTime(payload?.generated_at_utc)}</strong>
              </div>
              <div>
                <span>Next refresh (sec)</span>
                <input
                  className="asb-metric-input"
                  type="number"
                  inputMode="numeric"
                  min="5"
                  value={refreshIntervalInput}
                  onChange={(event) => setRefreshIntervalInput(event.target.value)}
                />
              </div>
            </div>
          </header>
          <nav className="asb-kinds-nav">
            {kinds.map((kind) => (
              <a
                key={kind.kind}
                href={`#kind-${kind.kind}`}
                className={`asb-kinds-nav-item asb-kind-${kind.kind}`}
              >
                {kind.kind}
              </a>
            ))}
          </nav>
          <div className="asb-filter">
            <div>
              <label htmlFor="asb-line-filter">Line number</label>
              <select
                id="asb-line-filter"
                value={lineFilter}
                onChange={(event) => setLineFilter(event.target.value)}
              >
                <option value="">All lines</option>
                {lineOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            {lineFilter && (
              <button type="button" onClick={() => setLineFilter("")}
              >
                Clear
              </button>
            )}
          </div>

          {error && <div className="asb-error">Error: {error}</div>}

          {loading ? (
            <div className="asb-loader">
              <div className="asb-spinner" />
              <p>Loading messages...</p>
            </div>
          ) : kinds.length === 0 ? (
            <div className="asb-empty">No messages available.</div>
          ) : !hasFilteredMessages ? (
            <div className="asb-empty">No messages match this line number.</div>
          ) : (
            <div className="asb-kinds">
              {filteredKinds.map((kind) => (
                <section className="asb-section" key={kind.kind} id={`kind-${kind.kind}`}>
                  <header className="asb-section-header">
                    <div>
                      <span className={`asb-badge asb-badge-${kind.kind}`}>
                        {kind.kind}
                      </span>
                      <h2>{kind.description || "No description"}</h2>
                    </div>
                    <div className="asb-section-stats">
                      <span>Total {kind.total ?? 0}</span>
                      <span>
                        Showing {kind.filteredCount} / {kind.items.length}
                      </span>
                    </div>
                  </header>
                  <div className="asb-section-controls">
                    <label htmlFor={`asb-per-kind-${kind.kind}`}>
                      Messages to show
                    </label>
                    <input
                      id={`asb-per-kind-${kind.kind}`}
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="50"
                      value={getPerKindValue(kind.kind)}
                      onChange={(event) =>
                        setPerKindByKind((prev) => ({
                          ...prev,
                          [kind.kind]: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="asb-cards">
                    {kind.filteredItems.length > 0 ? (
                      kind.filteredItems
                        .slice(0, getPerKindNumber(kind.kind) || kind.filteredItems.length)
                        .map((item, index) => (
                        <article className="asb-card" key={`${kind.kind}-${index}`}>
                          <div className="asb-card-meta">
                            <span>#{index + 1}</span>
                            <time>{formatTime(item.received_at_utc)}</time>
                            <span>stream={item.stream ?? ""}</span>
                          </div>
                          {kind.kind === "packages" ? (
                            <PackagesPanel body={item.body} />
                          ) : (
                            <pre className="asb-payload">{formatJson(item.body)}</pre>
                          )}
                        </article>
                      ))
                    ) : null}
                  </div>
                </section>
              ))}
            </div>
          )}
          {lastFetchedAt && (
            <footer className="asb-footer">
              Last fetched at {lastFetchedAt.toLocaleTimeString()} · Refreshing every 60 seconds
            </footer>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

const PackagesPanel = ({ body }) => {
  const packs = normalizePackagesPayload(body);
  return (
    <div className="asb-packages">
      {packs.length > 0 && (
        <table className="asb-packages-table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Order</th>
              <th>SKU</th>
              <th>Pack weight</th>
              <th>Nominal</th>
              <th>Status</th>
              <th>Produced</th>
            </tr>
          </thead>
          <tbody>
            {packs.map((pack, idx) => (
              <tr key={`${pack.id ?? "pack"}-${idx}`}>
                <td>{pack.lineNo ?? "N/A"}</td>
                <td>{pack.productionOrderNo ?? "N/A"}</td>
                <td>{pack.sku ?? "N/A"}</td>
                <td>{pack.packWeight ?? "N/A"}</td>
                <td>{pack.nominalWeight ?? "N/A"}</td>
                <td>{pack.status ?? "N/A"}</td>
                <td>{formatTime(pack.realProductionDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <pre className="asb-payload">{formatJson(body)}</pre>
    </div>
  );
};

export default AsbMessagesPage;
